import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import { CreatePatientDTO, Patient, UpdatePatientDTO } from "../../domain/Patient";
import type { InvitationLanguage, PatientInvitation } from "../../domain/PatientInvitation";
import type { ConsentCopySummary } from "../../../../shared/ui/consent/ConsentCopiesList";
import type { NoteReplyTherapist, PatientNoteTherapist } from "../../domain/PatientNote";

export type { NoteReplyTherapist, PatientNoteTherapist } from "../../domain/PatientNote";

export interface PatientConsentStatus {
  is_active: boolean;
  accepted_at?: string;
  revoked_at?: string;
  has_signed_document?: boolean;
}

export interface ListPatientsParams {
  offset?: number;
  limit?: number;
  search?: string;
  /** A training centre's students, by course and year of it. */
  course_id?: string;
  academic_year?: number;
}

/** Another course of the same student with the same mentor, and its record. */
export interface MentoredCourse {
  patient_id: string;
  course_id: string;
  name: string;
  internal_name: string;
  academic_year: number | null;
}

interface ListPatientsResponse {
  patients: Patient[];
  total: number;
}

export const PatientsAPI = {
  get: (id: string): Promise<AxiosResponse<Patient>> => apiClient.get(`/patients/${id}`),
  otherCourses: (id: string): Promise<AxiosResponse<MentoredCourse[]>> =>
    apiClient.get(`/patients/${id}/other-courses`),
  create: (data: CreatePatientDTO): Promise<AxiosResponse<Patient>> => apiClient.post("/patients/", data),
  update: (id: string, data: UpdatePatientDTO): Promise<AxiosResponse<Patient>> => apiClient.patch(`/patients/${id}`, data),
  delete: (id: string): Promise<AxiosResponse<void>> => apiClient.delete(`/patients/${id}`),
  list_by_therapist: (id: string, params?: ListPatientsParams): Promise<AxiosResponse<ListPatientsResponse>> =>
    apiClient.get(`/patients/therapist/${id}`, { params }),
  export: (id: string): Promise<AxiosResponse<Blob>> =>
    apiClient.get(`/export/patients/${id}`, { responseType: 'blob' }),
  getConsent: (id: string): Promise<AxiosResponse<PatientConsentStatus>> =>
    apiClient.get(`/patients/${id}/consent`),
  acceptConsentOnBehalf: (id: string): Promise<AxiosResponse<PatientConsentStatus>> =>
    apiClient.post(`/patients/${id}/consent/accept`),
  revokeConsentOnBehalf: (id: string): Promise<AxiosResponse<PatientConsentStatus>> =>
    apiClient.post(`/patients/${id}/consent/revoke`),
  getConsentDocumentUrl: (id: string): Promise<AxiosResponse<{ download_url: string }>> =>
    apiClient.get(`/patients/${id}/consent/document-url`),
  // The whole history: a consent can be signed more than once, and withdrawing
  // keeps what proved it — the controller is the one asked to show it.
  // The current document as the backend renders it for this patient: the same
  // text and the same names their portal shows, so a printed copy and a signed
  // one are the same document.
  getConsentDocumentPdf: (id: string): Promise<AxiosResponse<Blob>> =>
    apiClient.get(`/patients/${id}/consent/current.pdf`, { responseType: 'blob' }),
  getConsentCopies: (id: string): Promise<AxiosResponse<ConsentCopySummary[]>> =>
    apiClient.get(`/patients/${id}/consent/documents`),
  getConsentCopyUrl: (id: string, copyId: string): Promise<AxiosResponse<{ download_url: string }>> =>
    apiClient.get(`/patients/${id}/consent/documents/${copyId}/url`),
  getNotes: (id: string): Promise<AxiosResponse<PatientNoteTherapist[]>> =>
    apiClient.get(`/patients/${id}/notes`),
  markNoteRead: (patientId: string, noteId: string): Promise<AxiosResponse<{ id: string; read_by_therapist_at: string | null }>> =>
    apiClient.post(`/patients/${patientId}/notes/${noteId}/read`),
  getNoteReplies: (patientId: string, noteId: string): Promise<AxiosResponse<NoteReplyTherapist[]>> =>
    apiClient.get(`/patients/${patientId}/notes/${noteId}/replies`),
  createNoteReply: (patientId: string, noteId: string, body: string): Promise<AxiosResponse<NoteReplyTherapist>> =>
    apiClient.post(`/patients/${patientId}/notes/${noteId}/replies`, { body }),
  /** Portal invitations sent to this patient, newest first. */
  listInvitations: (patientId: string): Promise<AxiosResponse<PatientInvitation[]>> =>
    apiClient.get<PatientInvitation[]>(`/patients/${patientId}/invitations`),
  sendInvitation: (
    patientId: string,
    data: { email: string; language: InvitationLanguage }
  ): Promise<AxiosResponse<PatientInvitation>> =>
    apiClient.post<PatientInvitation>(`/patients/${patientId}/invitations`, data),
};
